import axios from 'axios';
import { logger } from '../utils/logger';
import { getAuthHeader } from '../utils/auth';
import { API_URL } from './api'; // Use normalized API_URL that always uses www.gotrutax.com

const INTEGRATION_CACHE_TTL_MS = 30 * 1000;
const integrationCache = new Map();

const getCacheKey = (companyId) => (companyId === null || companyId === undefined ? '__all__' : String(companyId));

/**
 * Service for managing platform integrations
 */
const platformService = {
  /**
   * List integrations for a company
   * @param {number} companyId - Company ID (optional)
   * @returns {Promise<Array>} Array of integrations
   */
  async listIntegrations(companyId = null, options = {}) {
    const { forceRefresh = false, skipCache = false } = options || {};
    const cacheKey = getCacheKey(companyId);
    const now = Date.now();

    if (!forceRefresh && !skipCache) {
      const cached = integrationCache.get(cacheKey);
      if (cached) {
        if (cached.data && cached.expiresAt > now) {
          return cached.data;
        }
        if (cached.promise) {
          return cached.promise;
        }
        integrationCache.delete(cacheKey);
      }
    } else if (forceRefresh) {
      integrationCache.delete(cacheKey);
    }

    const requestPromise = (async () => {
      try {
        const url = companyId ?
          `${API_URL}/api/v1/platforms/integrations?company_id=${companyId}` :
          `${API_URL}/api/v1/platforms/integrations`;

        // Removed verbose integration fetch logs for production hardening
        const response = await axios.get(url, { headers: getAuthHeader() });
        return response.data;
      } catch (error) {
        // Suppressed verbose error context logs; keep only high-level error below

        if (error.response?.status === 403 && companyId) {
          // Retry without company filter (log suppressed)
          const retryResponse = await axios.get(`${API_URL}/api/v1/platforms/integrations`, {
            headers: getAuthHeader()
          });
          // Retry successful (log suppressed)
          return retryResponse.data;
        }
        if (error.response && error.response.status === 404) {
          // 404 -> empty list (log suppressed)
          return [];
        }
        logger.error('[PlatformService] Error listing integrations:', error);
        throw error;
      }
    })();

    if (!skipCache) {
      integrationCache.set(cacheKey, { promise: requestPromise });
    }

    try {
      const data = await requestPromise;
      if (!skipCache) {
        integrationCache.set(cacheKey, {
          data,
          expiresAt: Date.now() + INTEGRATION_CACHE_TTL_MS,
        });
      }
      return data;
    } catch (error) {
      if (!skipCache) {
        integrationCache.delete(cacheKey);
      }
      throw error;
    }
  },

  /**
   * Get integration details
   * @param {number} integrationId - Integration ID
   * @returns {Promise<Object>} Integration details
   */
  async getIntegration(integrationId) {
    try {
      const response = await axios.get(`${API_URL}/api/v1/platforms/integrations/${integrationId}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      logger.error('Error getting integration:', error);
      throw error;
    }
  },

  /**
   * Create a new integration
   * @param {Object} integration - Integration data
   * @returns {Promise<Object>} Created integration
   */
  async createIntegration(integration) {
    try {
      const response = await axios.post(`${API_URL}/api/v1/platforms/integrations`, integration, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      logger.error('Error creating integration:', error);
      throw error;
    }
  },

  /**
   * Update an integration
   * @param {number} integrationId - Integration ID
   * @param {Object} integration - Updated integration data
   * @returns {Promise<Object>} Updated integration
   */
  async updateIntegration(integrationId, integration) {
    try {
      const response = await axios.put(`${API_URL}/api/v1/platforms/integrations/${integrationId}`, integration, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      logger.error('Error updating integration:', error);
      throw error;
    }
  },

  /**
   * Delete an integration
   * @param {number} integrationId - Integration ID
   * @returns {Promise<Object>} Result
   */
  async deleteIntegration(integrationId) {
    try {
      const response = await axios.delete(`${API_URL}/api/v1/platforms/integrations/${integrationId}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      logger.error('Error deleting integration:', error);
      throw error;
    }
  },

  /**
   * Verify platform credentials
   * @param {number} integrationId - Integration ID
   * @returns {Promise<Object>} Verification result
   */
  async verifyCredentials(integrationId) {
    try {
      const response = await axios.post(`${API_URL}/api/v1/platforms/integrations/${integrationId}/verify`, {}, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      logger.error('Error verifying credentials:', error);
      throw error;
    }
  },

  /**
   * Sync transactions from the platform
   * @param {number} integrationId - Integration ID
   * @param {Object} options - Sync options
   * @param {string} options.startDate - Start date (ISO format)
   * @param {string} options.endDate - End date (ISO format)
   * @param {boolean} options.background - Run in background
   * @returns {Promise<Object>} Sync result
   */
  async syncTransactions(integrationId, options = {}) {
    const url = `${API_URL}/api/v1/platforms/integrations/${integrationId}/sync`;
    
    // Normalize date keys to snake_case for backend compatibility
    let normalized = { ...options };
    try {
      if (Object.prototype.hasOwnProperty.call(options, 'startDate')) {
        normalized.start_date = options.startDate;
        delete normalized.startDate;
      }
      if (Object.prototype.hasOwnProperty.call(options, 'endDate')) {
        normalized.end_date = options.endDate;
        delete normalized.endDate;
      }

      logger.log(`[PlatformService] Making sync request to: ${url}`);
      logger.log(`[PlatformService] Request data:`, normalized);
      const response = await axios.post(url, normalized, {
        headers: getAuthHeader()
      });
      
      logger.log(`[PlatformService] Sync response received:`, response.data);
      return response.data;
    } catch (error) {
      logger.error(`[PlatformService] Error syncing transactions for integration ${integrationId}:`, error);
      logger.error(`[PlatformService] Error response:`, error.response?.data);
      logger.error(`[PlatformService] Error status:`, error.response?.status);
      throw error;
    }
  },

  /**
   * Calculate tax for pending transactions
   * @param {number} integrationId - Integration ID
   * @param {Object} options - Calculation options
   * @param {number} options.limit - Maximum number of transactions to process
   * @param {boolean} options.background - Run in background
   * @returns {Promise<Object>} Calculation result
   */
  async calculateTax(integrationId, options = {}) {
    const url = `${API_URL}/api/v1/platforms/integrations/${integrationId}/calculate-tax`;
    
    try {
      const response = await axios.post(url, options, {
        headers: getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      logger.error(`[FRONTEND_TAX] Error calculating tax for integration ${integrationId}:`, error);
      throw error;
    }
  },

  /**
   * List transactions for an integration
   * @param {number} integrationId - Integration ID
   * @param {Object} options - List options
   * @param {number} options.skip - Number of records to skip
   * @param {number} options.limit - Maximum number of records to return
   * @param {string} options.status - Filter by status
   * @returns {Promise<Array>} Array of transactions
   */
  async listTransactions(integrationId, options = {}) {
    const { skip = 0, limit = 100, status } = options;
    let url = `${API_URL}/api/v1/platforms/integrations/${integrationId}/transactions?skip=${skip}&limit=${limit}`;
    
    if (status) {
      url += `&status=${status}`;
    }
    
    try {
      const response = await axios.get(url, {
        headers: getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      logger.error('Error listing transactions:', error);
      throw error;
    }
  },

  /**
   * Get all transactions across all platforms with filtering and pagination
   * @param {Object} options - Query options
   * @param {number} options.skip - Number of records to skip
   * @param {number} options.limit - Maximum number of records to return
   * @param {string} options.platform - Filter by platform name
   * @param {string} options.status - Filter by status
   * @param {Date} options.startDate - Filter by start date
   * @param {Date} options.endDate - Filter by end date
   * @param {number} options.minAmount - Filter by minimum amount
   * @param {number} options.maxAmount - Filter by maximum amount
   * @param {number} options.minTaxAmount - Filter by minimum tax amount
   * @param {number} options.maxTaxAmount - Filter by maximum tax amount
   * @param {string} options.customerName - Filter by customer name
   * @returns {Promise<Object>} Object with transactions array and total count
   */
  async getAllTransactions(options = {}) {
    const {
      skip = 0,
      limit = 50,
      platform,
      status,
      startDate,
      endDate,
      minAmount,
      maxAmount,
      minTaxAmount,
      maxTaxAmount,
      customerName,
      companyId
    } = options;

    let url = `${API_URL}/api/v1/platforms/transactions?skip=${skip}&limit=${limit}`;
    
    // Add filters to URL
    if (platform) url += `&platform=${encodeURIComponent(platform)}`;
    // Backend expects `transaction_status` for this filter
    if (status) url += `&transaction_status=${encodeURIComponent(status)}`;
    if (startDate) url += `&start_date=${startDate.toISOString().split('T')[0]}`;
    if (endDate) url += `&end_date=${endDate.toISOString().split('T')[0]}`;
    if (minAmount) url += `&min_amount=${minAmount}`;
    if (maxAmount) url += `&max_amount=${maxAmount}`;
    if (minTaxAmount) url += `&min_tax_amount=${minTaxAmount}`;
    if (maxTaxAmount) url += `&max_tax_amount=${maxTaxAmount}`;
    if (customerName) url += `&customer_name=${encodeURIComponent(customerName)}`;
    if (companyId) url += `&company_id=${companyId}`;
    
    try {
      const response = await axios.get(url, {
        headers: getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      logger.error('Error fetching all transactions:', error);
      throw error;
    }
  },

  /**
   * Calculate tax for all transactions across all platforms
   * @param {Object} options - Calculation options
   * @param {boolean} [options.background=true] - Run calculation in background
  * @param {?number} [options.companyId] - Scope calculations to a single company
  * @param {boolean} [options.includeFailed=true] - Include failed statuses
  * @param {boolean} [options.allowRegistrationDefer=true] - Allow auto-registration retry
  * @param {Array<number>} [options.transactionIds] - Specific transaction IDs to process
   * @returns {Promise<Object>} Calculation result
   */
  async calculateAllTax(options = {}) {
    const {
      background = true,
      companyId = null,
      includeFailed = true,
      allowRegistrationDefer = true,
      transactionIds = null,
    } = options;

    const payload = {
      background,
      include_failed: includeFailed,
      allow_registration_defer: allowRegistrationDefer,
    };

    if (companyId) {
      payload.company_id = companyId;
    }

    if (Array.isArray(transactionIds) && transactionIds.length > 0) {
      payload.transaction_ids = transactionIds;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/platforms/transactions/calculate-tax`,
        payload,
        { headers: getAuthHeader() }
      );
      
      return response.data;
    } catch (error) {
      logger.error('Error calculating tax for all transactions:', error);
      throw error;
    }
  },

  /**
   * Counts of pending/failed/calculated transactions for a company, plus the top
   * failure reasons. Used after "Calculate All Tax" so a failed background run is
   * explained to the user instead of looking successful.
   * @param {?number} [companyId] - Company to summarise (defaults to the active company)
   * @returns {Promise<Object>} Status summary
   */
  async getTaxCalculationStatus(companyId = null) {
    const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/platforms/transactions/tax-calculation/status${query}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      logger.error('Error fetching tax calculation status:', error);
      throw error;
    }
  },

  /**
   * Retry failed tax calculations using the unified backend flow
   * @param {Object} options - Retry options
   * @param {?number} [options.companyId] - Filter by company
   * @param {boolean} [options.background=false] - Run in background
   * @param {Array<number>} [options.transactionIds] - Specific transaction IDs to retry
   * @param {Array<string>} [options.statuses] - Custom status list to retry
   * @param {boolean} [options.allowRegistrationDefer=true] - Allow registration defer
   * @returns {Promise<Object>} Retry result summary
   */
  async retryFailedTransactions(options = {}) {
    const {
      companyId = null,
      background = false,
      allowRegistrationDefer = true,
      transactionIds = null,
      statuses = null,
    } = options;

    const payload = {
      background,
      allow_registration_defer: allowRegistrationDefer,
    };

    if (companyId) {
      payload.company_id = companyId;
    }

    if (Array.isArray(transactionIds) && transactionIds.length > 0) {
      payload.transaction_ids = transactionIds;
    }

    if (Array.isArray(statuses) && statuses.length > 0) {
      payload.statuses = statuses;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/platforms/transactions/retry-failed-tax-calculation`,
        payload,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      logger.error('Error retrying failed tax calculations:', error);
      throw error;
    }
  },

  /**
   * Override the product tax code for a specific transaction and recalculate
   */
  async overrideTransactionTaxCode(transactionId, taxCode) {
    const url = `${API_URL}/api/v1/platforms/transactions/${transactionId}/override-tax-code`;
    const response = await axios.post(url, { tax_code: String(taxCode || '') }, { headers: getAuthHeader() });
    return response.data;
  },

  /**
   * Upload CSV file to create integration
   * @param {FormData} formData - Form data containing file and company_id
   * @returns {Promise<Object>} Upload result
   */
  async uploadCSV(formData) {
    try {
      logger.log('[PlatformService] Uploading CSV file...');
      const response = await axios.post(`${API_URL}/api/v1/platforms/csv/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      logger.log('[PlatformService] CSV upload successful:', response.data);
      return response.data;
    } catch (error) {
      logger.error('[PlatformService] Error uploading CSV:', error);
      throw error;
    }
  },

  /**
   * Import QuickBooks integration (OAuth flow)
   */
  async importQuickBooks(company_id, store_id) {
    try {
      const response = await axios.post(`${API_URL}/api/v1/platforms/quickbooks/connect`, { company_id, store_id }, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      logger.error('Error importing QuickBooks:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Validate QuickBooks OAuth token
   * @param {number} integrationId - Integration ID
   * @returns {Promise<Object>} Validation result with {valid: boolean, reason: string}
   */
  async validateQuickBooksToken(integrationId) {
    try {
      logger.log(`[PlatformService] Validating QuickBooks token for integration ${integrationId}`);
      const response = await axios.get(`${API_URL}/api/v1/platforms/quickbooks/validate-token/${integrationId}`, {
        headers: getAuthHeader()
      });
      logger.log(`[PlatformService] Token validation result:`, response.data);
      return response.data;
    } catch (error) {
      logger.error('Error validating QuickBooks token:', error);
      // If there's an error calling the endpoint, assume token is invalid
      return {
        valid: false,
        reason: error.response?.data?.detail || error.message || 'Failed to validate token'
      };
    }
  },

  /**
   * Start Shopify OAuth flow
   */
  async authorizeShopify(company_id, shop, redirect_uri) {
    try {
      const response = await axios.post(`${API_URL}/api/v1/platforms/shopify/oauth/authorize`, { company_id, shop, redirect_uri }, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      logger.error('Error authorizing Shopify:', error);
      throw error;
    }
  },

  /**
   * Handle Shopify OAuth callback
   */
  async shopifyOAuthCallback(code, state, shop, queryParams) {
    try {
      // query_params lets the backend verify Shopify's HMAC over the redirect
      const response = await axios.post(`${API_URL}/api/v1/platforms/shopify/oauth/callback`, { code, state, shop, query_params: queryParams || null }, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      logger.error('Error in Shopify OAuth callback:', error);
      throw error;
    }
  },

  /**
   * Invalidate cached integrations for a specific company (or all)
   * @param {number | null | undefined} companyId
   */
  invalidateIntegrationsCache(companyId = null) {
    const normalizedId = typeof companyId === 'number' ? companyId : null;
    const cacheKey = getCacheKey(normalizedId);
    integrationCache.delete(cacheKey);
  },

  invalidateAllIntegrationsCache() {
    integrationCache.clear();
  }
};

export default platformService; 
